/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback, useMemo, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { differenceBy } from 'lodash';
import classNames from 'classnames';
import { Stack } from '@fluentui/react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Link } from '@bingads-webui-react/fabric-with-signal';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { SuggestedItemsMultiSelect, SelectableSuggestedItem, MoreSuggestionsInlineButton } from '@bingads-webui-campaign-react/pill-item-components';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';

import { getKeywordGroupStyles } from './style/keyword-group-style';
import { useCircularBufferViewWithSelected, MAX_INLINE_RECOMMENDATIONS_PER_PAGE } from './util/use-circular-buffer-view';
import { KeywordGroupViewAllPanel } from './keyword-group-view-all-panel';

const SelectableSuggestedItemInternal = ({
  item, index, onItemToggle, selectedItems,
}) => {
  const activity = useContext(ActivityContext);
  const onKeywordToggle = useCallback((itemIndex, itemText) => {
    onItemToggle(item, itemIndex, itemText);
    // eslint-disable-next-line no-unused-expressions
    activity && activity.signal(`onAddSidePanelGroupKeyword - ${JSON.stringify({
      GroupKeyword: item,
    })}`);
  }, [item, onItemToggle, activity]);

  return (
    <SelectableSuggestedItem
      itemText={item.Keyword}
      itemIndex={index}
      onToggle={onKeywordToggle}
      isSelected={selectedItems.some(s => s.key === item.key)}
    />
  );
};

SelectableSuggestedItemInternal.propTypes = {
  item: PropTypes.shape({
    Keyword: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  index: PropTypes.number.isRequired,
  onItemToggle: PropTypes.func.isRequired,
  selectedItems: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

export const KeywordGroup = ({
  groupName,
  groupItems,
  selectedItems,
  onToggle,
  onAddAll,
  onBulkChange,
}) => {
  const activity = useContext(ActivityContext);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getKeywordGroupStyles);
  const currentSelectedItems = useMemo(() => _.filter(selectedItems, s => _.some(groupItems, item => item.key === s.key)), [selectedItems, groupItems]);
  const sortedGroupItems = useMemo(() => _.sortBy(groupItems, g => !_.some(currentSelectedItems, item => item.key === g.key)), [groupItems, currentSelectedItems]);
  const unSelectedItems = useMemo(() => _.last(sortedGroupItems, sortedGroupItems.length - currentSelectedItems.length), [sortedGroupItems, currentSelectedItems]);
  const [availableInlineRecommendations, canMoveNext, moveNext] = useCircularBufferViewWithSelected(unSelectedItems, currentSelectedItems);
  const onClickRefresh = useCallback(() => {
    if (activity) {
      activity.signal(`onRefreshSidePanelGroupKeywords - ${groupName} - ${JSON.stringify({
        GroupKeywordsSize: groupItems.length,
      })}`);
    }
    moveNext();
  }, [moveNext, activity, groupName, groupItems.length]);
  const onPanelOpen = useCallback(() => {
    setIsPanelOpen(true);
    if (activity) {
      activity.signal(`onOpenSidePanelGroupDetailsGrid - ${groupName}`);
    }
  }, [setIsPanelOpen, activity, groupName]);
  const onAddAllClick = useCallback(() => {
    onAddAll(groupItems);
    // eslint-disable-next-line no-unused-expressions
    activity && activity.signal(`onAddAllSidePanelGroupKeywords - ${JSON.stringify({
      GroupKeywords: groupItems,
    })}`);
  }, [groupItems, onAddAll, activity]);
  const onSelectionChange = useCallback((newSelectedItems) => {
    if (newSelectedItems) {
      onBulkChange(differenceBy(currentSelectedItems, newSelectedItems, 'key'), differenceBy(newSelectedItems, currentSelectedItems, 'key'));
    }
    setIsPanelOpen(false);
  }, [currentSelectedItems, onBulkChange]);
  const onRemoveAllClick = useCallback(() => {
    onBulkChange(groupItems, []);
    if (activity) {
      activity.signal(`onRemoveAllSidePanelGroupKeywords - ${groupName} - ${JSON.stringify({
        GroupKeywords: currentSelectedItems,
      })}`);
    }
  }, [groupItems, onBulkChange, activity, currentSelectedItems, groupName]);

  const renderSelectableSuggestedItem = useCallback((item, index, onItemToggle) => (
    <SelectableSuggestedItemInternal
      item={item}
      index={index}
      onItemToggle={onItemToggle}
      selectedItems={selectedItems}
    />), [selectedItems]);
  const renderButtons = useCallback(() => {
    if (groupItems.length > MAX_INLINE_RECOMMENDATIONS_PER_PAGE) {
      return (
        <>
          <MoreSuggestionsInlineButton onClick={onClickRefresh} useStyleFluent2 disabled={!canMoveNext} />
          <Link className={classNames(classes.checkAllLink, 'check-all')} onClick={onPanelOpen}>{i18n.getString(_TL_('+{{count}} keywords'), { count: groupItems.length - MAX_INLINE_RECOMMENDATIONS_PER_PAGE })}</Link>
        </>
      );
    }
    return null;
  }, [canMoveNext, classes.checkAllLink, groupItems.length, i18n, onClickRefresh, onPanelOpen]);

  return (
    <div className={`${classes.container} keyword-group`}>
      <div className={classes.title}>{groupName}</div>
      <SuggestedItemsMultiSelect
        suggestedItems={availableInlineRecommendations}
        onSuggestedItemToggle={onToggle}
        renderSelectableSuggestedItem={renderSelectableSuggestedItem}
        renderButtons={renderButtons}
      />
      <Stack horizontal wrap tokens={{ childrenGap: 16 }}>
        <Stack.Item key="add-all-suggestions-button">
          <Link className={classNames(classes.addAllLink, 'add-all')} disabled={unSelectedItems.length === 0} onClick={onAddAllClick}>{i18n.getString(_TL_('Add all'))}</Link>
        </Stack.Item>
        {
          currentSelectedItems.length > 0 && unSelectedItems.length > 0 && (
          <Stack.Item key="remove-suggestions-button">
            <Link className={classNames(classes.addAllLink, 'remove')} onClick={onRemoveAllClick}>
              {currentSelectedItems.length === 1 ? i18n.getString(_TL_('Remove (1 keyword)')) : i18n.getString(_TL_('Remove ({{count}} keywords)'), { count: currentSelectedItems.length })}
            </Link>
          </Stack.Item>
          )
        }
        {
          currentSelectedItems.length > 0 && unSelectedItems.length === 0 && (
          <Stack.Item key="remove-all-suggestions-button">
            <Link className={classNames(classes.addAllLink, 'remove-all')} onClick={onRemoveAllClick}>
              {currentSelectedItems.length === 1 ? i18n.getString(_TL_('Remove (1 keyword)')) : i18n.getString(_TL_('Remove all ({{count}} keywords)'), { count: currentSelectedItems.length })}
            </Link>
          </Stack.Item>
        )}
      </Stack>
      <KeywordGroupViewAllPanel
        isOpen={isPanelOpen}
        allData={groupItems}
        selectedData={currentSelectedItems}
        onClose={onSelectionChange}
        i18n={i18n}
      />
    </div>
  );
};

KeywordGroup.propTypes = {
  groupItems: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  selectedItems: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onToggle: PropTypes.func.isRequired,
  groupName: PropTypes.string.isRequired,
  onAddAll: PropTypes.func.isRequired,
  onBulkChange: PropTypes.func.isRequired,
};
